import type {ReactNode} from 'react'
import {ChevronDownIcon, ChevronRightIcon, AlertIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import styles from './NewToolHeader.module.css'
import {clsx} from 'clsx'
import {useSessionLogsContext} from '../Context'
import {sendEvent} from '@github-ui/hydro-analytics'
import {WithShimmerEffect} from '@github-ui/copilot-chat-visuals/WithShimmerEffect'
import {StatusTextSlider} from '@github-ui/agent-sessions-interface/components/StatusTextSlider'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ToolHeader} from '@github-ui/agent-sessions-interface/components/ToolHeader'

export interface ToolHeaderProps {
  /**
   * The leading visual element to display before the title (e.g., an icon)
   */
  leadingVisual?: ReactNode
  /**
   * The trailing visual element to display after the title (e.g., an indicator icon)
   */
  trailingVisual?: ReactNode
  /**
   * Inline content rendered immediately after the title text (e.g., duration)
   */
  titleSuffix?: ReactNode
  /**
   * Renders the title suffix inside the main text run instead of as a separate flex item.
   */
  inlineTitleSuffix?: boolean
  /**
   * Indicates whether the file is in a collapsed state
   */
  isCollapsed?: boolean
  /**
   * Title details of tool-driven titles
   */
  title: string
  /**
   * The path to the file, if part of the args
   */
  path?: string
  /**
   * Indicates whether the tool has references rendered as children (for chat variant)
   */
  hasReferences?: boolean
  /**
   * Indicates whether the tool is in an error state
   */
  isError?: boolean
  /**
   * Indicates whether the tool is in a pending state
   */
  isPending?: boolean
  /**
   * Callback for handling modifier + click to expand/collapse all tools
   */
  onModifierClick?: (expand: boolean) => void
  /**
   * Callback for handling regular click to toggle this tool
   */
  onToggle?: () => void
  /**
   * Indicates whether the tool is part of a group
   */
  isInGroup?: boolean
  /**
   * Indicates whether this header is for a group (not a child inside a group)
   */
  isGroupHeader?: boolean
  asSummary?: boolean
  /**
   * Muted status text shown inline after the chevron (e.g., current tool call name)
   */
  statusText?: string
}

/**
 * Props that can be passed to the Tool component to configure the ToolHeader.
 * This is a subset of ToolHeaderProps, containing only the customizable visual elements.
 */
export type ToolHeaderPropsType = Pick<
  ToolHeaderProps,
  'leadingVisual' | 'trailingVisual' | 'titleSuffix' | 'inlineTitleSuffix'
>

export function NewToolHeader({
  leadingVisual,
  trailingVisual,
  titleSuffix,
  inlineTitleSuffix = false,
  title,
  path,
  isCollapsed,
  hasReferences = true,
  isError = false,
  isPending = false,
  isInGroup = false,
  isGroupHeader = false,
  asSummary = false,
  statusText,
  onModifierClick,
  onToggle,
}: ToolHeaderProps) {
  const {variant, onToolExpand} = useSessionLogsContext()

  const handleClick = (event: React.MouseEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if ((event.altKey || event.metaKey) && onModifierClick) {
      event.preventDefault()
      event.stopPropagation()
      // If currently collapsed, expand all; if expanded, collapse all
      onModifierClick(!!isCollapsed)

      sendEvent('agent_sessions.task_view', {
        target: 'TOOL_HEADER_MODIFIER',
        action: isCollapsed ? 'EXPAND_ALL' : 'COLLAPSE_ALL',
        tool_title: title,
      })

      return false // Prevent further event handling
    } else {
      // Regular click - toggle this tool
      onToggle?.()
      onToolExpand?.(!!isCollapsed)

      sendEvent('agent_sessions.task_view', {
        target: 'TOOL_HEADER',
        action: isCollapsed ? 'EXPAND' : 'COLLAPSE',
        tool_title: title,
      })
    }
  }

  if (variant === 'mission-control' || variant === 'chat') {
    if (variant === 'mission-control' && isFeatureEnabled('copilot_mc_unified_tool_calls')) {
      return (
        <ToolHeader
          leadingVisual={leadingVisual}
          trailingVisual={trailingVisual}
          titleSuffix={titleSuffix}
          inlineTitleSuffix={inlineTitleSuffix}
          title={title}
          path={path}
          isPending={isPending}
          isError={isError}
          isCollapsed={isCollapsed}
          hasReferences={hasReferences}
          isInGroup={isInGroup}
          isGroupHeader={isGroupHeader}
          statusText={statusText}
          onToggle={() => {
            onToggle?.()
            onToolExpand?.(!!isCollapsed)
            sendEvent('agent_sessions.task_view', {
              target: 'TOOL_HEADER',
              action: isCollapsed ? 'EXPAND' : 'COLLAPSE',
              tool_title: title,
            })
          }}
          onModifierClick={
            onModifierClick
              ? expand => {
                  onModifierClick(expand)
                  sendEvent('agent_sessions.task_view', {
                    target: 'TOOL_HEADER_MODIFIER',
                    action: isCollapsed ? 'EXPAND_ALL' : 'COLLAPSE_ALL',
                    tool_title: title,
                  })
                }
              : undefined
          }
        />
      )
    }

    return (
      <div className={styles.headerContainer}>
        <button
          type="button"
          className={clsx(styles.missionControl, {
            [styles.inGroup]: isInGroup,
            [styles.groupHeader]: isGroupHeader,
            [styles.nonInteractive]: !hasReferences,
          })}
          onClick={hasReferences ? handleClick : undefined}
          aria-disabled={!hasReferences}
          aria-expanded={!isCollapsed}
          tabIndex={hasReferences ? 0 : -1}
        >
          {leadingVisual ? (
            <span className={styles.leadingVisualContainer}>
              <span className={styles.leadingVisualIcon}>{leadingVisual}</span>
              {hasReferences && (
                <ChevronRightIcon
                  size={12}
                  className={styles.leadingVisualChevron}
                  data-expanded={!isCollapsed}
                  aria-hidden="true"
                />
              )}
            </span>
          ) : null}
          {isPending ? (
            <div className={styles.text}>
              <WithShimmerEffect className={styles.shimmerInline} innerClassName={styles.shimmerInline}>
                <span className={clsx(styles.title, {[styles.titleInGroup]: isInGroup})}>{title}</span>
              </WithShimmerEffect>
              {path && (
                <>
                  {' '}
                  <span className={styles.path}>{path}</span>
                </>
              )}
              {inlineTitleSuffix && titleSuffix ? <> {titleSuffix}</> : null}
            </div>
          ) : (
            <span className={styles.text}>
              <span className={clsx(styles.title, {[styles.titleInGroup]: isInGroup})}>{title}</span>
              {path && (
                <>
                  {' '}
                  <span className={styles.path}>{path}</span>
                </>
              )}
              {inlineTitleSuffix && titleSuffix ? <> {titleSuffix}</> : null}
            </span>
          )}
          {!inlineTitleSuffix && titleSuffix}
          {hasReferences && !leadingVisual && (
            <ChevronRightIcon size={12} className={styles.toggleIcon} data-expanded={!isCollapsed} aria-hidden="true" />
          )}
          {statusText && <StatusTextSlider text={statusText} />}
          {isError && <AlertIcon size={16} className="color-fg-attention flex-shrink-0" />}
        </button>
        {trailingVisual ? <div className={styles.trailingVisualContainer}>{trailingVisual}</div> : null}
      </div>
    )
  }

  const defaultTitle = <span className={styles.defaultTitle}>{title}</span>

  return (
    <Button
      as={asSummary ? 'summary' : 'button'}
      block
      labelWrap
      className={clsx('Button', styles.button)}
      alignContent="start"
      aria-disabled={!hasReferences}
      aria-expanded={hasReferences ? !isCollapsed : undefined}
      aria-label={titleSuffix ? undefined : title}
      tabIndex={hasReferences ? undefined : -1}
      leadingVisual={hasReferences ? isCollapsed ? <ChevronRightIcon /> : <ChevronDownIcon /> : undefined}
      trailingVisual={isError ? <AlertIcon size={16} className="color-fg-attention flex-shrink-0" /> : null}
      onClick={hasReferences ? handleClick : undefined}
    >
      <span className={styles.defaultContent}>
        {isPending ? (
          <WithShimmerEffect as="span" className={styles.shimmerInline} innerClassName={styles.shimmerInline}>
            {defaultTitle}
          </WithShimmerEffect>
        ) : (
          defaultTitle
        )}
        {path && <span className="text-mono fgColor-default">{path}</span>}
        {titleSuffix}
      </span>
    </Button>
  )
}
